// src/pages/ExtensionList.js
import React, { useEffect, useState } from "react";
import { useAuthCheck } from "../components/Auth";
import { api } from "../components/Api";
import Loading from "../components/Loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

export default function ExtensionList() {
    const [extensions, setExtensions] = useState([]);
    const [filteredExtensions, setFilteredExtensions] = useState([]);
    const [licenses, setLicenses] = useState([]);
    const [collections, setCollections] = useState([]);
    const [selectedLicense, setSelectedLicense] = useState("");
    const [selectedCollection, setSelectedCollection] = useState("");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { checkToken } = useAuthCheck();

    useEffect(() => { checkToken(); }, [checkToken]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [extRes, licRes, collRes] = await Promise.all([
                api.get("/extensions"),
                api.get("/license-cards"),
                api.get("/collections")
            ]);

            setExtensions(extRes.data);
            setFilteredExtensions(extRes.data);
            setLicenses(licRes.data);
            setCollections(collRes.data);
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la récupération des données.");
        } finally {
            setLoading(false);
        }
    };

    const handleLicenseChange = (e) => {
        const licId = e.target.value;
        setSelectedLicense(licId);
        setSelectedCollection(""); // réinitialiser la collection

        // Filtrer les extensions selon licence
        let filtered = extensions;
        if (licId) {
            filtered = filtered.filter(ext => ext.collection?.license_card_id === parseInt(licId));
        }
        setFilteredExtensions(filtered);
    };

    const handleCollectionChange = (e) => {
        const collId = e.target.value;
        setSelectedCollection(collId);

        let filtered = extensions;
        if (selectedLicense) {
            filtered = filtered.filter(ext => ext.collection?.license_card_id === parseInt(selectedLicense));
        }
        if (collId) {
            filtered = filtered.filter(ext => ext.collection_id === parseInt(collId));
        }
        setFilteredExtensions(filtered);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Voulez-vous vraiment supprimer cette extension ?")) return;

        try {
            await api.delete(`/extensions/${id}`);
            alert("Extension supprimée avec succès.");
            setExtensions(prev => prev.filter(e => e.id !== id));
            setFilteredExtensions(prev => prev.filter(e => e.id !== id));
        } catch (err) {
            console.error(err);
            if (err.response?.status === 409) {
                alert("Impossible de supprimer cette extension car elle est utilisée par des cartes.");
            } else {
                alert("Erreur lors de la suppression de l'extension.");
            }
        }
    };

    const token = localStorage.getItem("token");
    if (!token || localStorage.getItem("role") > 2) {
        navigate("/");
    }

    // Collections filtrées par licence sélectionnée
    const filteredCollections = selectedLicense
        ? collections.filter(c => c.license_card_id === parseInt(selectedLicense))
        : collections;

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-2xl font-bold mb-4 dark:text-white">Liste des Extensions</h1>

            <div className="flex space-x-4 mb-4">
                <button
                    onClick={() => navigate("/add-extension")}
                    className="bg-blue-400 hover:bg-blue-500 text-white font-semibold rounded px-4 py-2"
                >
                    Ajouter une extension
                </button>
                <div>
                    <select
                        value={selectedLicense}
                        onChange={handleLicenseChange}
                        className="border p-2 rounded dark:bg-gray-700 bg-white"
                    >
                        <option value="">-- Toutes les licences --</option>
                        {licenses.map(l => (
                            <option key={l.id} value={l.id}>{l.license_card}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <select
                        value={selectedCollection}
                        onChange={handleCollectionChange}
                        className="border p-2 rounded dark:bg-gray-700 bg-white"
                    >
                        <option value="">-- Toutes les collections --</option>
                        {filteredCollections.map(c => (
                            <option key={c.id} value={c.id}>{c.collection_name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <Loading message="Chargement des extensions..." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border text-sm text-center">
                        <thead className="bg-gray-100 dark:bg-gray-800">
                            <tr>
                                <th className="border px-2 py-1">Licence</th>
                                <th className="border px-2 py-1">Collection</th>
                                <th className="border px-2 py-1">Extension</th>
                                <th className="border px-2 py-1">Supprimer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredExtensions.map((ext, index) => {
                                const bgColor = index % 2 === 0 ? "dark:bg-gray-600 bg-gray-050" : "dark:bg-gray-700 bg-gray-100";
                                return (
                                    <tr key={ext.id} className={`${bgColor}`}>
                                        <td className="border px-2 py-1">{ext.collection?.license_card?.license_card || ""}</td>
                                        <td className="border px-2 py-1">{ext.collection?.collection_name || ""}</td>
                                        <td className="border px-2 py-1">{ext.extension_name}</td>
                                        <td className="border px-2 py-1">
                                            <button
                                                onClick={() => handleDelete(ext.id)}
                                                className="text-red-500 hover:text-red-700 text-2xl"
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredExtensions.length === 0 && (
                                <tr>
                                    <td colSpan="3" className="text-gray-500 py-3">
                                        Aucune extension trouvée pour cette licence.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
